import React, { useState } from "react";
import useAxios from 'axios-hooks';
import TextField from "../../../Forms/TextField";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import LogoUploader from "./LogoUploader";
import { TooltipHost, Dropdown } from '@fluentui/react';
import { Icon } from '@fluentui/react/lib/Icon';

const logoOptions = [
    { key: 'Color', text: 'Color' },
    { key: 'White', text: 'White' }
];

const logoFieldMap = {
    desktop: {
        retinaColor: 'retinaLogoColor',
        standardColor: 'standardLogoColor',
        retinaWhite: 'retinaLogoWhite',
        standardWhite: 'standardLogoWhite',
        retinaNav: 'retinaLogoForNav',
        standardNav: 'standardLogoForNav',
        pxWidth: 'pxWidthStandardLogoDesktopTablet',
        cdnPathColor: 'IMAGE/Logos/Color',
        cdnPathWhite: 'IMAGE/Logos/White',
    },
    mobile: {
        retinaColor: 'retinaMobileAltLogoColor',
        standardColor: 'standardMobileAltLogoColor',
        retinaWhite: 'retinaMobileAltLogoWhite',
        standardWhite: 'standardMobileAltLogoWhite',
        retinaNav: 'retinaMobileLogoForNav',
        standardNav: 'standardMobileLogoForNav',
        pxWidth: 'pxWidthStandardMobileLogoMobile',
        cdnPathColor: 'IMAGE/Mobile/Logos/Color',
        cdnPathWhite: 'IMAGE/Mobile/Logos/White',
    }
};

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const LogoImageSettings = ({ values, setFieldValue, type = 'desktop' }) => {
    const fields = logoFieldMap[type];
    const [logoCobrandCopy, setLogoCobrandCopy] = useState(true);
    const [selectedLogoType, setSelectedLogoType] = useState();
    const [{ data: cobrands }] = useAxios('/internal/cobrands');

    const cobrandsOptions = cobrands?.map(u => ({ value: u.cobrandId, label: u.name }));

    const [{ data: cobrandSettings }, fetchCobrandSettings] = useAxios(
        { url: '/internal/cobrand/launcher', method: 'GET' },
        { manual: true }
    );

    const handleCobrandChange = async (option) => {
        setLogoCobrandCopy(option.value);
        const result = await fetchCobrandSettings({
            url: `/internal/cobrand/launcher/${option.value}`,
        });

        if (!result.error) {
            const settings = result.data;
            setFieldValue(fields.retinaColor, settings[fields.retinaColor]);
            setFieldValue(fields.standardColor, settings[fields.standardColor]);
            setFieldValue(fields.retinaWhite, settings[fields.retinaWhite]);
            setFieldValue(fields.standardWhite, settings[fields.standardWhite]);
            setFieldValue(fields.retinaNav, settings[fields.retinaNav]);
            setFieldValue(fields.standardNav, settings[fields.standardNav]);
            setFieldValue(fields.pxWidth, settings[fields.pxWidth]);
        }
    };

    const handleLogoTypeChange = (event, option) => {
        const selected = option.key;
        setSelectedLogoType(selected);

        if (selected === 'White') {
            setFieldValue(fields.retinaNav, values[fields.retinaWhite] || '');
            setFieldValue(fields.standardNav, values[fields.standardWhite] || '');
        } else {
            setFieldValue(fields.retinaNav, values[fields.retinaColor] || '');
            setFieldValue(fields.standardNav, values[fields.standardColor] || '');
        }
    };

    return (
        <div className="panel panel-default">
            <div className="panel-heading panel-heading-cobrand">
                <div className="panel-h">{type === 'desktop' ? 'Desktop Images/Logos' : 'Mobile Images/Logos'}</div>
                <div className="cobrand-select">
                    <Select
                        components={{ MenuList: CustomMenuList }}
                        options={cobrandsOptions}
                        placeholder="Copy from Cobrand"
                        name="clientId"
                        value={cobrandsOptions?.find(opt => opt.value === logoCobrandCopy)}
                        onChange={(option) => handleCobrandChange(option)}
                        isSearchable
                        styles={{
                            menuList: (base) => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
                            container: (base) => ({ ...base, width: 200 }),
                            control: (base) => ({ ...base, border: "1px solid #aaaaaa" }),
                        }}
                    />
                </div>
            </div>

            <div className="panel-body">
                <fieldset style={{ marginBottom: 30 }}>
                    <div className="displayflex" style={{ justifyContent: 'center' }}>
                        <div className="form-inline">
                            Upload Retina Logo Color <span style={{color:'red'}}>*</span>
                            <LogoUploader cdnPath={fields.cdnPathColor} setFieldValue={setFieldValue} color="Color" type={type}/>
                        </div>
                        <div style={{ marginLeft: 80 }} className="form-inline">
                            Upload Retina Logo White <span style={{color:'red'}}>*</span>
                            <LogoUploader cdnPath={fields.cdnPathWhite} setFieldValue={setFieldValue} color="White" type={type}/>
                        </div>
                        <TooltipHost
                            content={
                                <div>
                                    <p className="body-s" style={{ marginTop: 0 }}>
                                        Please provide a logo that satisfies the following:
                                    </p>
                                    <ol className="pl-5 mb-8 list-decimal" style={{ paddingLeft: 16 }}>
                                        <li>High resolution transparent .png file</li>
                                        <li>Ideal Retina range (which is logo at 2x view size): 96-112px (height)  x  260-560px (width), standard view size is 50% of retina.</li>
                                        <li>Max Retina range: 132px (height), 640px (width). (logo will show at 50% of this size on site)</li>
                                        <li>For mobile logos, they will be 62% size of standard desktop logo.</li>
                                    </ol>
                                </div>
                            }
                        >
                            <Icon iconName="Info" style={{ cursor: 'pointer', fontSize: 20, marginLeft: 30 }} />
                        </TooltipHost>
                    </div>
                </fieldset>

                <fieldset>
                        <div className="form-inline">
                            <TextField
                                label="Retina Logo Color"
                                name={fields.retinaColor}
                                className="bigger-input"
                                value={values[fields.retinaColor]}
                                onChange={(e, val) => setFieldValue(fields.retinaColor, val)}
                                placeholder="CDN URL slug"
                                required
                            />
                        </div>
                        <div className="form-inline">
                            <TextField
                                label="Standard Logo Color"
                                name={fields.standardColor}
                                className="bigger-input"
                                value={values[fields.standardColor]}
                                onChange={(e, val) => setFieldValue(fields.standardColor, val)}
                                placeholder="CDN URL slug"
                                required
                            />
                        </div>
                        <div className="form-inline">
                            <TextField
                                label="Retina Logo White"
                                name={fields.retinaWhite}
                                className="bigger-input"
                                value={values[fields.retinaWhite]}
                                onChange={(e, val) => setFieldValue(fields.retinaWhite, val)}
                                placeholder="CDN URL slug"
                                required
                            />
                        </div>
                        <div className="form-inline">
                            <TextField
                                label="Standard Logo White"
                                name={fields.standardWhite}
                                className="bigger-input"
                                value={values[fields.standardWhite]}
                                onChange={(e, val) => setFieldValue(fields.standardWhite, val)}
                                placeholder="CDN URL slug"
                                required
                            />
                        </div>

                    {values[fields.retinaColor] && values[fields.retinaWhite] && (!values[fields.retinaNav] && !values[fields.standardNav]) && (
                        <div className="form-inline">
                            <Dropdown
                                label="Select Logo For Nav"
                                options={logoOptions}
                                selectedKey={selectedLogoType}
                                onChange={handleLogoTypeChange}
                                required
                                styles={{ dropdown: { width: 300 } }}
                            />
                        </div>
                    )}

                        <div className="form-inline">
                            <TextField
                                label="Retina Logo For Nav"
                                name={fields.retinaNav}
                                className="bigger-input"
                                value={values[fields.retinaNav]}
                                onChange={(e, val) => setFieldValue(fields.retinaNav, val)}
                                placeholder="CDN URL slug"
                                required
                            />
                        </div>
                        <div className="form-inline">
                            <TextField
                                label="Standard Logo For Nav"
                                name={fields.standardNav}
                                className="bigger-input"
                                value={values[fields.standardNav]}
                                onChange={(e, val) => setFieldValue(fields.standardNav, val)}
                                placeholder="CDN URL slug"
                                required
                            />
                        </div>
                        <div className="form-inline">
                            <TextField
                                label="Px Width"
                                name={fields.pxWidth}
                                className="bigger-input"
                                type="number"
                                value={values[fields.pxWidth]}
                                onChange={(e, val) => {
                                    const parsed = parseInt(val, 10);
                                    if (!isNaN(parsed) && parsed >= 1 && parsed <= 320) {
                                        setFieldValue(fields.pxWidth, parsed);
                                    }
                                }}
                                min={1}
                                max={320}
                                placeholder="Numeric value: Ideal size (130–280px w). Max size (320px)"
                                required
                            />
                        </div>
                </fieldset>
            </div>
        </div>
    );
};

export default LogoImageSettings;
